import { createContext, useState, useEffect, useContext } from 'react';
import useAPI from '../hooks/useAPI';
import { toast } from 'react-toastify';
import { getCookies } from '../utility/cookies';
import AuthContext from './AuthContext';

interface NotificationContextType {
  notificationData: INotification[];
  loadMore: () => void;
  handleNewNotification: (notification: INotification) => void;
  hasMore: boolean;
  isLoading: boolean;
  hasNewNotifications: boolean;
  setHasNewNotification: (val: boolean) => void;
  receiveRequestQueue: INotification[];
  addToReceiveRequestQueue: (notification: INotification) => void;
  removeFromReceiveRequestQueue: (notificationId: string) => void;
  isAppLoaded: boolean;
  setAppLoaded: (loaded: boolean) => void;
}

export interface INotification {
  id: string;
  roomId: string;
  title: string;
  message: string;
  status: string;
  action: string;
  type: string;
  comment: string | null;
  isSeen: boolean;
  createdAt: string;
  updatedAt: string;
}

const NotificationContext = createContext<NotificationContextType>({
  notificationData: [],
  loadMore: () => {},
  handleNewNotification: () => {},
  hasMore: false,
  isLoading: false,
  hasNewNotifications: false,
  setHasNewNotification: () => {},
  receiveRequestQueue: [],
  addToReceiveRequestQueue: () => {},
  removeFromReceiveRequestQueue: () => {},
  isAppLoaded: false,
  setAppLoaded: () => {},
});

export default NotificationContext;

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [notificationData, setNotificationData] = useState<INotification[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasNewNotifications, setHasNewNotification] = useState(false);
  const [receiveRequestQueue, setReceiveRequestQueue] = useState<
    INotification[]
  >([]);
  const [isAppLoaded, setAppLoaded] = useState(false);
  const { userData: user } = useContext(AuthContext);

  // Helper to get notification id (id or _id)
  const getNotificationId = (notification: any) =>
    notification.id || notification._id;

  // On initial fetch, populate receiveRequestQueue with all unread 'Receive Request' notifications
  const { refetch, isLoading } = useAPI({
    autoFetch: !!user?.id && !!process.env.REACT_APP_NOTIFICATION_API_KEY,
    enabled: !!user?.id && !!process.env.REACT_APP_NOTIFICATION_API_KEY,
    url: `${process.env.REACT_APP_NOTIFICATION_ENGINE_API}/getNotifications?pageSize=${10}&pageNumber=${page}`,
    headers: {
      apiKey: process.env.REACT_APP_NOTIFICATION_API_KEY,
      sender: user?.id,
    },
    method: 'GET',
    onSuccess: data => {
      if (page === 1) {
        setNotificationData(data.result.messages || []);
        // On first load, check for unread 'Receive Request' notifications
        if (!isAppLoaded) {
          const unreadReceiveRequests = (data.result.messages || []).filter(
            (notification: INotification) =>
              notification.title === 'Receive Request' && !notification.isSeen,
          );
          // De-duplicate by id/_id
          const uniqueQueue = unreadReceiveRequests.filter(
            (n: INotification, idx: number, arr: INotification[]) =>
              arr.findIndex(
                (x: INotification) =>
                  getNotificationId(x) === getNotificationId(n),
              ) === idx,
          );
          if (uniqueQueue.length > 0) {
            setReceiveRequestQueue(uniqueQueue);
          }
        }
      } else {
        setNotificationData(prev => [
          ...prev,
          ...(data?.result?.messages || []),
        ]);
      }
      setHasMore(page < data?.result?.totalPages);
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });

  const loadMore = () => {
    if (!isLoading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleNewNotification = (notification: INotification) => {
    setNotificationData(prevData => {
      const newData = [...prevData];
      newData.unshift(notification);
      return newData;
    });

    // If it's a "Receive Request" notification and app is loaded, add to queue
    if (notification.title === 'Receive Request' && isAppLoaded) {
      addToReceiveRequestQueue(notification);
    }
  };

  // Add to queue with de-duplication by id/_id
  const addToReceiveRequestQueue = (notification: INotification) => {
    setReceiveRequestQueue(prev => {
      const notificationId = getNotificationId(notification);
      if (prev.some(n => getNotificationId(n) === notificationId)) {
        console.warn(
          '[addToReceiveRequestQueue] Duplicate notification not added:',
          notification,
        );
        return prev;
      }
      const newQueue = [...prev, notification];
      console.log(
        '[addToReceiveRequestQueue] Added notification:',
        notification,
        'New queue:',
        newQueue,
      );
      return newQueue;
    });
  };

  // Remove from queue by id/_id
  const removeFromReceiveRequestQueue = (notificationId: string) => {
    if (!notificationId) {
      console.warn(
        '[removeFromReceiveRequestQueue] Called with undefined notificationId!',
      );
      return;
    }
    setReceiveRequestQueue(prev => {
      const newQueue = prev.filter(
        n => getNotificationId(n) !== notificationId,
      );
      console.log(
        '[removeFromReceiveRequestQueue] Removed notificationId:',
        notificationId,
        'New queue:',
        newQueue,
      );
      return newQueue;
    });
  };

  useEffect(() => {
    refetch();
  }, [page]);

  return (
    <NotificationContext.Provider
      value={{
        notificationData,
        loadMore,
        hasMore,
        isLoading,
        handleNewNotification,
        hasNewNotifications,
        setHasNewNotification,
        receiveRequestQueue,
        addToReceiveRequestQueue,
        removeFromReceiveRequestQueue,
        isAppLoaded,
        setAppLoaded,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
